import React, { useState, useCallback } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AppSidebar from '../layout/AppSidebar';
import AppHeader from '../layout/AppHeader';
import SubscriptionBanner from '../billing/SubscriptionBanner';
import { FeatureErrorBoundary } from '../layout/ErrorBoundary';
import { PageProvider } from '../../context/PageContext';
import { Eye, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { getViewAsOrgId, setViewAsOrgId } from '../../utils/api';
import api from '../../utils/api';

/**
 * Layout route for authenticated app pages.
 *
 * Renders a vertical sidebar, a gradient header with breadcrumbs,
 * and the child route content. Redirects to /login when unauthenticated.
 *
 * User and auth state are sourced from AuthContext.
 *
 * @param {object}   props
 * @param {string}   [props.featureName] - Optional override for error boundary label.
 */
const ProtectedLayout = ({ featureName }) => {
  const { isAuthenticated, user, logout, loading } = useAuth();
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(() => (
    typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(max-width: 1023px)').matches
  ));
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('sidebar-collapsed') === 'true';
    } catch {
      return false;
    }
  });

  // Impersonation state — persisted in sessionStorage across navigations
  const viewAsOrgParam = new URLSearchParams(location.search).get('viewAsOrg');
  const [viewAsOrgInfo, setViewAsOrgInfo] = useState(() => {
    try {
      const stored = sessionStorage.getItem('viewAsOrgInfo');
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });

  // Restore impersonation header from sessionStorage on mount
  React.useEffect(() => {
    const storedOrgId = sessionStorage.getItem('viewAsOrgId');
    if (storedOrgId) {
      setViewAsOrgId(storedOrgId);
    }
  }, []);

  React.useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mediaQuery = window.matchMedia('(max-width: 1023px)');
    const handleChange = (event) => {
      setIsMobile(event.matches);
      if (!event.matches) setMobileSidebarOpen(false);
    };
    setIsMobile(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  React.useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname, location.search]);

  // When URL param is present (first load or org switch), store and fetch info
  React.useEffect(() => {
    if (viewAsOrgParam) {
      setViewAsOrgId(viewAsOrgParam);
      sessionStorage.setItem('viewAsOrgId', viewAsOrgParam);
      if (!viewAsOrgInfo || viewAsOrgInfo.org_id !== viewAsOrgParam) {
        // Fetch org info for the banner (GET-like, no audit side-effect).
        // The actual audit log is written by SuperAdminOrgSwitcher.handleConfirmImpersonate.
        api.get('/super-admin/organizations/search', { params: { q: '', limit: 100 } })
          .then(({ data }) => {
            const match = (data.data || []).find(o => o.org_id === viewAsOrgParam);
            if (match) {
              const info = { org_id: viewAsOrgParam, company_name: match.company_name, email: match.owner_email, account_count: match.account_count };
              setViewAsOrgInfo(info);
              sessionStorage.setItem('viewAsOrgInfo', JSON.stringify(info));
            }
          })
          .catch(() => {});
      }
    }
  }, [viewAsOrgParam, viewAsOrgInfo]);

  const exitImpersonation = useCallback(async () => {
    try {
      await api.post('/super-admin/impersonate/stop', null, { params: { org_id: getViewAsOrgId() } });
    } catch { /* best-effort */ }
    setViewAsOrgId(null);
    sessionStorage.removeItem('viewAsOrgId');
    sessionStorage.removeItem('viewAsOrgInfo');
    const params = new URLSearchParams(location.search);
    params.delete('viewAsOrg');
    const qs = params.toString();
    window.location.href = `${location.pathname}${qs ? '?' + qs : ''}`;
  }, [location]);

  if (!isAuthenticated && !loading) {
    return <Navigate to="/login" replace />;
  }

  const onLogout = logout;
  const toggleSidebar = () => {
    if (isMobile) {
      setMobileSidebarOpen((prev) => !prev);
      return;
    }
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('sidebar-collapsed', String(next));
      } catch {
        // localStorage unavailable — ignore
      }
      return next;
    });
  };

  return (
    <PageProvider>
    <div className="flex flex-col h-screen overflow-hidden">
      {/* Impersonation Banner */}
      {viewAsOrgInfo && (
        <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-2 text-center text-sm flex-shrink-0 z-[60]">
          <div className="flex items-center justify-center gap-2">
            <Eye className="w-4 h-4" />
            <span>
              <strong>Viewing as:</strong> {viewAsOrgInfo.company_name} ({viewAsOrgInfo.email})
              {viewAsOrgInfo.plan_type && (
                <span className="ml-2 opacity-75">
                  • {viewAsOrgInfo.plan_type.replace('_', ' ')} • {viewAsOrgInfo.account_count} accounts
                </span>
              )}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={exitImpersonation}
              className="ml-4 text-white hover:bg-white/20 h-7 px-2"
            >
              <X className="w-4 h-4 mr-1" />
              Exit
            </Button>
          </div>
        </div>
      )}

      {/* Global banners */}
      {user?.subscription_active === false && <SubscriptionBanner user={user} />}

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        {isMobile ? (
          <Sheet open={mobileSidebarOpen} onOpenChange={setMobileSidebarOpen}>
            <SheetContent
              side="left"
              className="w-64 gap-0 p-0 sm:max-w-64 [&>button]:hidden"
            >
              <SheetHeader className="sr-only">
                <SheetTitle>Application Navigation</SheetTitle>
                <SheetDescription>Navigate between application sections.</SheetDescription>
              </SheetHeader>
              <AppSidebar
                user={user}
                onLogout={onLogout}
                collapsed={false}
                onToggleCollapse={() => setMobileSidebarOpen(false)}
              />
            </SheetContent>
          </Sheet>
        ) : (
          <div className="relative z-40 flex-shrink-0">
            <AppSidebar
              user={user}
              onLogout={onLogout}
              collapsed={sidebarCollapsed}
              onToggleCollapse={toggleSidebar}
            />
          </div>
        )}

        {/* Main column: header + scrollable content */}
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          <AppHeader
            user={user}
            sidebarCollapsed={sidebarCollapsed}
            onToggleSidebar={toggleSidebar}
          />

          <main className="flex-1 overflow-y-auto bg-muted dark:bg-background">
            <FeatureErrorBoundary featureName={featureName || 'Page'}>
              <Outlet context={{ user, onLogout }} />
            </FeatureErrorBoundary>
          </main>
        </div>
      </div>
    </div>
    </PageProvider>
  );
};

export default ProtectedLayout;
